/**
 * @file     conversationStore.ts
 * @purpose  Persist the active Relta chat conversation. Two layers, mirroring
 *           aiHistoryMemory: localStorage (instant, works anonymous) and, for
 *           signed-in users, the account (user_ai_conversations) so the history
 *           rail and cross-device resume work. Saves are debounced fire-and-forget.
 */

import { secureFetch } from '@/services/anti-scraping/secureFetch';
import type { ReltaConversationState, ReltaConversationSummary } from './types';

const LOCAL_KEY = 'relta.aiChat.v1';         // legacy single-blob key (migrated once)
const LOCAL_LIST_KEY = 'relta.aiChat.list.v1';
const DEFAULT_SCOPE = 'general';
const MAX_LOCAL_PER_SCOPE = 5;
const MAX_LOCAL_TOTAL = 30;

export interface LocalBlob {
  localId: string;
  conversationId: string | null;
  scope: string;
  state: ReltaConversationState;
}

/** Stable per-conversation id for the local recent list (anon has no server id). */
export function newLocalId(): string {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID(); } catch { /* older browser */ }
  return `loc-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

/** Text-only + card-keys projection (never full card blobs): keeps localStorage
 *  tiny so a big chat can't blow quota and drop the transcript. Cards re-hydrate
 *  from the audited API on load; mirrors the server's ADR-009 shape. */
function leanState(state: ReltaConversationState): ReltaConversationState {
  return {
    ...state,
    messages: state.messages.map((m) => ({
      role: m.role,
      content: m.content,
      ...(m.error ? { error: true } : {}),
      ...(m.matches?.length
        ? { hadMatches: true as const, presentedKeys: m.matches.map((c) => c.listingKey).slice(0, 5) }
        : m.presentedKeys?.length
          ? { hadMatches: true as const, presentedKeys: m.presentedKeys }
          : {}),
    })),
  };
}

function normalizeState(s: Partial<ReltaConversationState> | undefined): ReltaConversationState | null {
  if (!s || !Array.isArray(s.messages)) return null;
  return {
    messages: s.messages,
    filters: s.filters ?? {},
    count: typeof s.count === 'number' ? s.count : null,
    offset: typeof s.offset === 'number' ? s.offset : 0,
    notes: Array.isArray(s.notes) ? s.notes.filter((n) => typeof n === 'string') : [],
    seenListingKeys: Array.isArray(s.seenListingKeys) ? s.seenListingKeys.filter((k) => typeof k === 'string') : [],
    updatedAt: typeof s.updatedAt === 'number' ? s.updatedAt : 0,
  };
}

function readList(): LocalBlob[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(LOCAL_LIST_KEY);
    const rawList = raw ? (JSON.parse(raw) as unknown[]) : [];
    let blobs: LocalBlob[] = (Array.isArray(rawList) ? rawList : [])
      .map((b) => {
        const bb = b as Partial<LocalBlob>;
        const state = normalizeState(bb.state);
        if (!state || !bb.localId) return null;
        return {
          localId: String(bb.localId),
          conversationId: typeof bb.conversationId === 'string' ? bb.conversationId : null,
          scope: typeof bb.scope === 'string' && bb.scope ? bb.scope : DEFAULT_SCOPE,
          state,
        };
      })
      .filter((b): b is LocalBlob => b != null);
    // One-time migration of the legacy single blob into the list.
    if (blobs.length === 0) {
      const legacy = window.localStorage.getItem(LOCAL_KEY);
      if (legacy) {
        const b = JSON.parse(legacy) as Partial<LocalBlob>;
        const state = normalizeState(b?.state);
        if (state && state.messages.length) {
          blobs = [{
            localId: newLocalId(),
            conversationId: typeof b.conversationId === 'string' ? b.conversationId : null,
            scope: DEFAULT_SCOPE,
            state,
          }];
        }
      }
    }
    return blobs.sort((a, b) => b.state.updatedAt - a.state.updatedAt);
  } catch {
    return [];
  }
}

function writeList(list: LocalBlob[]): void {
  try {
    window.localStorage.setItem(LOCAL_LIST_KEY, JSON.stringify(list.slice(0, MAX_LOCAL_TOTAL)));
  } catch { /* quota */ }
}

/** Most-recent local conversation (mount resume). */
export function loadLocalConversation(scope = DEFAULT_SCOPE): LocalBlob | null {
  return readList().find((blob) => blob.scope === scope) ?? null;
}

/** A specific local conversation by its localId (previous-chats pill). */
export function loadLocalById(localId: string, scope = DEFAULT_SCOPE): LocalBlob | null {
  return readList().find((blob) => blob.localId === localId && blob.scope === scope) ?? null;
}

/** Summaries for the previous-chats list. Same shape as the signed-in server
 *  summaries so the rail renders local + server chats identically. */
export function listLocalSummaries(scope = DEFAULT_SCOPE): ReltaConversationSummary[] {
  return readList()
    .filter((blob) => blob.scope === scope)
    .filter((b) => b.state.messages.some((m) => m.role === 'user'))
    .map((b) => ({ id: b.localId, title: titleFor(b.state) || 'Chat', updated_at: new Date(b.state.updatedAt || Date.now()).toISOString() }));
}

export function saveLocalConversation(
  localId: string,
  conversationId: string | null,
  state: ReltaConversationState,
  scope = DEFAULT_SCOPE,
): void {
  if (typeof window === 'undefined') return;
  const list = readList().filter((blob) => blob.localId !== localId);
  list.unshift({ localId, conversationId, scope, state: leanState(state) });
  const keptInScope = list
    .filter((blob) => blob.scope === scope)
    .sort((a, b) => b.state.updatedAt - a.state.updatedAt)
    .slice(0, MAX_LOCAL_PER_SCOPE);
  const otherScopes = list.filter((blob) => blob.scope !== scope);
  writeList([...keptInScope, ...otherScopes].sort((a, b) => b.state.updatedAt - a.state.updatedAt));
}

export function removeLocalConversation(localId: string, scope = DEFAULT_SCOPE): void {
  writeList(readList().filter((blob) => blob.localId !== localId || blob.scope !== scope));
}

export function clearLocalConversation(): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.removeItem(LOCAL_LIST_KEY); window.localStorage.removeItem(LOCAL_KEY); } catch { /* noop */ }
}

export function titleFor(state: ReltaConversationState): string {
  const first = state.messages.find((m) => m.role === 'user');
  return (first?.content ?? '').slice(0, 80);
}

/** One-shot adoption of an anonymous transcript into the account at sign-in.
 *  After this, the server owns the conversation (the respond route persists
 *  every turn); the client never writes conversation rows again. Card data is
 *  stripped: v2 conversations store text only. */
export async function adoptAnonymousConversation(state: ReltaConversationState): Promise<string | null> {
  try {
    const textOnly = {
      v: 2,
      messages: state.messages.map((m) => ({
        role: m.role,
        content: m.content,
        ...(m.error ? { error: true } : {}),
        ...(m.matches?.length ? { hadMatches: true, presentedKeys: m.matches.map((c) => c.listingKey).slice(0, 5) } : {}),
      })),
      filters: state.filters,
      count: state.count,
      offset: state.offset,
      notes: state.notes ?? [],
      updatedAt: state.updatedAt,
    };
    const res = await secureFetch('/api/ai-chat/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: titleFor(state), state: textOnly }),
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { id?: string };
    return data.id ?? null;
  } catch {
    return null; // localStorage still has it; the next turn creates a row
  }
}

export async function listConversations(): Promise<ReltaConversationSummary[]> {
  try {
    const res = await fetch('/api/ai-chat/conversations');
    if (!res.ok) return [];
    const data = (await res.json()) as { conversations?: ReltaConversationSummary[] };
    return data.conversations ?? [];
  } catch {
    return [];
  }
}

export async function fetchConversation(id: string): Promise<{ id: string; title: string; state: ReltaConversationState } | null> {
  try {
    const res = await fetch(`/api/ai-chat/conversations?id=${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const data = (await res.json()) as { conversation?: { id: string; title: string; state: ReltaConversationState } | null };
    return data.conversation ?? null;
  } catch {
    return null;
  }
}

export async function deleteConversation(id: string): Promise<void> {
  try {
    await secureFetch(`/api/ai-chat/conversations?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
  } catch {
    // best-effort
  }
}
